import { StoredPromptContextEnum } from '@ztechapps/common.contract'

/**
 * Metadata for a single stored prompt context.
 *
 * Single source of truth for prompt configuration used by backend (AiPromptService),
 * AI intent classification (aiDescription), and admin UI (name, description, required).
 */
export type StoredPromptConfigItem = {
  /** App Setting key used to store/retrieve the prompt */
  key: string
  /** Human-readable name shown in the Admin UI */
  name: string
  /** Description shown in the Admin UI explaining the prompt's purpose */
  description: string
  /** Description used by the AI to classify user intent and select the appropriate context */
  aiDescription: string
  /** Whether a prompt value is required for this context */
  required: boolean
  /**
   * Whether the admin AI chat intent classifier may pick this context.
   * `true` = chat-facing intent the user can trigger by typing or
   * uploading (e.g., CreateOrder, CreatePurchaseOrder). `false` = internal
   * action prompt invoked server-side only and never surfaced to the chat
   * (e.g., GenerateProductDescription, ExtractAddress). The classifier
   * allowlist is derived at runtime by filtering `StoredPromptConfigs` on
   * `classifiable === true`. Encode any attachment / format cues directly
   * in `aiDescription` — the classifier reads it verbatim.
   */
  classifiable: boolean
}

/**
 * Consolidated stored prompt configuration — single source of truth.
 *
 * Merges data previously spread across StoredPromptKeys, StoredPromptContextDescriptions,
 * and the admin UI PROMPT_CONFIGS into one authoritative record.
 */
export const StoredPromptConfigs: Record<StoredPromptContextEnum, StoredPromptConfigItem> = {
  [StoredPromptContextEnum.CreateOrder]: {
    key: 'AI_PROMPT_CREATE_ORDER_FILE_ANALYSIS',
    name: 'Create Order File Analysis',
    description:
      'System prompt for analyzing files to create orders. AI uses this when it detects user wants to create an order from a PO, quote, or order document. Required for AI file analysis.',
    aiDescription:
      'User wants to create a sales order from a customer document — typically a PDF or image of a purchase order, quote, or order document the user has attached or is describing in chat.',
    required: true,
    classifiable: true,
  },
  [StoredPromptContextEnum.CreatePurchaseOrder]: {
    key: 'AI_PROMPT_CREATE_PURCHASE_ORDER_FILE_ANALYSIS',
    name: 'Create Purchase Order File Analysis',
    description:
      'System prompt for analyzing vendor documents (packing slips, vendor POs) to create purchase orders. AI uses this when the user uploads a vendor document or asks to create a PO. Required for AI file analysis on the PO flow.',
    aiDescription:
      'User wants to create a purchase order from a vendor document — typically a PDF or image of a packing slip, vendor PO, or marketplace order confirmation the user has attached or is describing in chat.',
    required: true,
    classifiable: true,
  },
  [StoredPromptContextEnum.CreateProduct]: {
    key: 'AI_PROMPT_CREATE_PRODUCT',
    name: 'Create Product',
    description:
      'System prompt for proposing a new Panel Punches product from admin chat or an attached customer communication (email, PDF, image, pasted text). AI extracts target dimensions, shape, and corner radius, finds the closest base product via metadata search, and proposes a clone — the admin reviews and confirms before anything is committed.',
    aiDescription:
      'User wants to create one or more new products from explicit dimensions or an attached customer communication — extract target dimensions (vertical inches, horizontal inches), shape, and corner radius from the request or attachment, search the existing catalog for the closest match per target with a tolerance window, propose the new product(s) for admin review, and stop without committing until the admin explicitly confirms.',
    required: true,
    classifiable: true,
  },
  [StoredPromptContextEnum.GenerateProductDescription]: {
    key: 'AI_PROMPT_GENERATE_PRODUCT_DESCRIPTION',
    name: 'Generate Product Description',
    description:
      'System prompt for generating product descriptions. Used by the product description AI action to create compelling product copy from product information.',
    aiDescription: 'Generate product description from product information',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.ExtractAddress]: {
    key: 'AI_PROMPT_EXTRACT_ADDRESS',
    name: 'Extract Address',
    description:
      'System prompt for extracting addresses from text. Used to parse and structure shipping/billing addresses from order chat messages.',
    aiDescription: 'Extract and parse shipping/billing addresses from text',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.GenerateProductName]: {
    key: 'AI_PROMPT_GENERATE_PRODUCT_NAME',
    name: 'Generate Product Name',
    description:
      'System prompt for generating descriptive product names from product codes. Used by the product name AI action to create human-readable names from SKU/part numbers.',
    aiDescription: 'Generate descriptive product names from product codes',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.GenerateBlogTags]: {
    key: 'AI_PROMPT_GENERATE_BLOG_TAGS',
    name: 'Generate Blog Tags',
    description: 'System prompt for generating relevant tags for blog posts based on content.',
    aiDescription: 'Generate relevant tags for blog posts based on content',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.GenerateBlogSummary]: {
    key: 'AI_PROMPT_GENERATE_BLOG_SUMMARY',
    name: 'Generate Blog Summary',
    description: 'System prompt for generating summary for blog posts based on content.',
    aiDescription: 'Generate summary for blog posts based on content',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.SummarizeTranscription]: {
    key: 'AI_PROMPT_SUMMARIZE_TRANSCRIPTION',
    name: 'Summarize Transcription',
    description:
      'System prompt for summarizing video/audio transcriptions. Used by blog transcription processing to generate concise summaries with key points and topics.',
    aiDescription: 'Summarize video or audio transcriptions into key points and topics',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.ExtractDocumentCode]: {
    key: 'AI_PROMPT_EXTRACT_DOCUMENT_CODE',
    name: 'Extract Document Code',
    description:
      'Extracts document codes (Order, Purchase Order PO-, Work Order PROD-, Project PROJ-, Task T-, Logistic MOV-) from uploaded photos. Used by the mobile app cross-domain photo scanning to identify and auto-attach images to documents.',
    aiDescription:
      'Extract document codes (Order, Purchase Order, Work Order, Project, Task, Logistic) from uploaded images/photos for auto-attachment',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.FormalizeCommunication]: {
    key: 'AI_PROMPT_FORMALIZE_COMMUNICATION',
    name: 'Formalize Communication',
    description: 'System prompt for transforming informal communication into professional business language.',
    aiDescription: 'Transform informal text into professional business communication',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.StorefrontSearch]: {
    key: 'AI_PROMPT_STOREFRONT_SEARCH',
    name: 'Storefront Search (AI)',
    description:
      "Base system prompt for the storefront natural-language product and blog search assistant. Router appends the tenant's public PRODUCT_SPECIFICATIONS metadata key list at request time.",
    aiDescription:
      'User is searching the Panel Punches storefront product catalog — natural-language queries for die sets, punches, tools by name, code, or dimensional metadata (e.g., "round die set 37mm horizontal").',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.HaulerAudioPickupExtraction]: {
    key: 'AI_PROMPT_HAULER_AUDIO_PICKUP_EXTRACTION',
    name: 'Hauler Audio Pickup Extraction',
    description: 'System prompt for one-shot voice pickup field extraction from audio transcripts',
    aiDescription: 'Extract milk pickup fields from a voice transcript',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.HaulerManifestClassification]: {
    key: 'AI_PROMPT_HAULER_MANIFEST_CLASSIFICATION',
    name: 'Hauler Manifest Classification',
    description: 'System prompt for classifying manifest type by logo/header before extraction',
    aiDescription: 'Classify milk hauler manifest type from image',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.HaulerManifestExtractionLanco]: {
    key: 'AI_PROMPT_HAULER_MANIFEST_EXTRACTION_LANCO',
    name: 'Hauler Manifest Extraction (Lanco)',
    description: 'System prompt for extracting pickups from Lanco cooperative manifests',
    aiDescription: 'Extract milk pickup fields from a Lanco paper manifest',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.HaulerManifestExtractionMvmpca]: {
    key: 'AI_PROMPT_HAULER_MANIFEST_EXTRACTION_MVMPCA',
    name: 'Hauler Manifest Extraction (MVMPCA)',
    description: 'System prompt for extracting pickups from MVMPCA cooperative manifests',
    aiDescription: 'Extract milk pickup fields from a MVMPCA paper manifest',
    required: true,
    classifiable: false,
  },
  [StoredPromptContextEnum.HaulerManifestExtractionUmwo30]: {
    key: 'AI_PROMPT_HAULER_MANIFEST_EXTRACTION_UMWO30',
    name: 'Hauler Manifest Extraction (UMWO30)',
    description: 'System prompt for extracting pickups from Upper Midwest Order 30 manifests',
    aiDescription: 'Extract milk pickup fields from an Upper Midwest Order 30 paper manifest',
    required: true,
    classifiable: false,
  },
}

/**
 * Maps stored prompt contexts to their corresponding App Setting keys.
 *
 * Derived from StoredPromptConfigs for backward compatibility.
 * Used by AiPromptService to retrieve the appropriate prompt for a given context.
 */
export const StoredPromptKeys: Record<StoredPromptContextEnum, string> = Object.fromEntries(
  Object.entries(StoredPromptConfigs).map(([context, config]) => [context, config.key])
) as Record<StoredPromptContextEnum, string>

/**
 * Human-readable descriptions of each stored prompt context.
 *
 * Derived from StoredPromptConfigs for backward compatibility.
 * Used by the AI to classify user intent and select the appropriate context.
 */
export const StoredPromptContextDescriptions: Record<StoredPromptContextEnum, string> = Object.fromEntries(
  Object.entries(StoredPromptConfigs).map(([context, config]) => [context, config.aiDescription])
) as Record<StoredPromptContextEnum, string>

/**
 * AI Prompt Setting Keys
 *
 * These keys are used to store and retrieve AI prompts from AppSettings.
 * Prompts can be customized per tenant via the Admin UI.
 *
 * Shared between frontend (admin UI) and backend (AiPromptService).
 */
export const AiPromptKeys = {
  /**
   * Default chat system prompt.
   * Used when no attachment is provided.
   */
  CHAT_DEFAULT: 'AI_PROMPT_CHAT_DEFAULT',
} as const

export type AiPromptKey = (typeof AiPromptKeys)[keyof typeof AiPromptKeys]

/**
 * Chat Session Storage Configuration
 *
 * Constants for managing AI chat session persistence in localStorage.
 */

/**
 * Storage key for chat sessions in localStorage
 * Shared across all users on the same browser
 * COUPLING: mirrored in apps/panelpunches/api-test/src/browser/support/chatSessionStorage.ts (`SESSION_STORAGE_KEY`)
 */
export const SESSION_STORAGE_KEY = 'ai-chat-sessions'

/**
 * Schema version for session data structure
 * Used for future migrations
 * COUPLING: mirrored in apps/panelpunches/api-test/src/browser/support/chatSessionStorage.ts (`SESSION_SCHEMA_VERSION`)
 */
export const SESSION_SCHEMA_VERSION = 1

/**
 * Maximum number of sessions to store
 * Limited to stay within localStorage ~5MB limit
 */
export const MAX_SESSIONS = 5

/**
 * Maximum length for auto-generated session titles
 */
export const MAX_TITLE_LENGTH = 50

/**
 * Default title for new sessions before first message
 */
export const DEFAULT_SESSION_TITLE = 'New Chat'

/**
 * The userId the in-app AI chat runs tools as when no user is signed in. The conversation reply tools refuse it,
 * so every writer and that guard must share this one value.
 */
export const ANONYMOUS_USER_ID = 'anonymous'

/**
 * Row limit every AI list tool applies to its query: `default` when the model
 * passes none, `max` as the cap on what it may ask for.
 */
export const AI_TOOL_LIST_LIMIT = { default: 10, max: 50 } as const

/**
 * Prefix of the text an MCP tool returns on failure. The server writes it (BaseMcpTool.createErrorResponse) and the
 * chat UI reads it back to show the tool call as failed, so both must share this one value.
 */
export const MCP_ERROR_PREFIX = 'Error:'

/** Attempts a model gets to fix a failed query pipeline before a query tool gives up. */
export const MAX_RETRY_COUNT = 5

/** The text a query tool answers with when the pipeline matched nothing. */
export const EMPTY_RESULTS_MESSAGE =
  'No results found. The query executed successfully but returned no matching documents.'
